import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle, Circle, Clock, XCircle, Shield, Award, Upload,
  FileText, Camera, ChevronRight, Loader2, User, BookOpen
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVEL_STYLES = {
  none: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
  bronze: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300' },
  silver: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-300' },
  gold: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-400' },
};

export default function PanditVerifyPage({ user }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('dharmbook_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Document form
  const [idProofType, setIdProofType] = useState('aadhaar');
  const [idProofNumber, setIdProofNumber] = useState('');
  const [certificateInfo, setCertificateInfo] = useState('');

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/pandit/verification/status`, { headers });
      setStatus(res.data);
    } catch {
      toast.error('Failed to load verification status');
    } finally {
      setLoading(false);
    }
  };

  const submitDocuments = async () => {
    if (!idProofNumber.trim()) {
      toast.error('Please enter your ID proof number');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('id_proof_type', idProofType);
      formData.append('id_proof_number', idProofNumber);
      formData.append('certificate_info', certificateInfo);
      await axios.post(`${API}/pandit/verification/documents`, formData, { headers });
      toast.success('Documents submitted for review!');
      fetchStatus();
    } catch {
      toast.error('Failed to submit documents');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  const steps = status?.steps || {};
  const allStepsCompleted = steps.profile?.completed && steps.exam?.completed && steps.documents?.completed && steps.admin_review?.completed;
  const ls = LEVEL_STYLES[status?.level || 'none'];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="pandit-verify-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 text-white px-4 py-6">
        <div className="max-w-lg mx-auto">
          <h1 className="text-xl font-bold mb-1">Pandit Verification</h1>
          <p className="text-orange-100 text-sm">Complete all steps to get your Verified badge</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Current Level Badge */}
        {status?.is_verified && (
          <div className={`${ls.bg} border-2 ${ls.border} rounded-2xl p-4 mb-6 flex items-center gap-4`} data-testid="verification-level">
            <div className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
              <Award className={`w-7 h-7 ${ls.text}`} />
            </div>
            <div>
              <p className={`font-bold text-lg ${ls.text}`}>{status.level_label}</p>
              <p className="text-xs text-slate-500">DharmBook Certified Pandit</p>
            </div>
          </div>
        )}

        {/* Step Progress */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden mb-6">
          <div className="p-4 border-b border-stone-100">
            <h2 className="font-bold text-slate-800">Verification Steps</h2>
          </div>

          {/* Step 1: Profile */}
          <StepItem
            icon={<User className="w-5 h-5" />}
            label="Create Pandit Profile"
            completed={steps.profile?.completed}
            active={!steps.profile?.completed}
            onAction={() => navigate('/pandit/register')}
            actionLabel="Create Profile"
          />

          {/* Step 2: Exam */}
          <StepItem
            icon={<BookOpen className="w-5 h-5" />}
            label="Pass Knowledge Exam"
            subtitle={steps.exam?.completed ? `Score: ${steps.exam.score}%` : '70% required to pass'}
            completed={steps.exam?.completed}
            active={steps.profile?.completed && !steps.exam?.completed}
            onAction={() => navigate('/pandit/exam')}
            actionLabel="Take Exam"
          />

          {/* Step 3: Documents */}
          <StepItem
            icon={<FileText className="w-5 h-5" />}
            label="Submit Documents"
            subtitle={steps.documents?.completed ? 'Documents submitted' : 'ID proof & certificates'}
            completed={steps.documents?.completed}
            active={steps.exam?.completed && !steps.documents?.completed}
          />

          {/* Step 4: Admin Review */}
          <StepItem
            icon={<Shield className="w-5 h-5" />}
            label="Admin Review"
            subtitle={
              steps.admin_review?.status === 'approved' ? 'Approved!'
              : steps.admin_review?.status === 'rejected' ? `Rejected: ${steps.admin_review.rejection_reason || 'Contact support'}`
              : steps.admin_review?.status === 'pending_review' ? 'Under review...'
              : 'Awaiting documents'
            }
            completed={steps.admin_review?.status === 'approved'}
            rejected={steps.admin_review?.status === 'rejected'}
            active={false}
            isLast
          />
        </div>

        {/* Document Upload Form - Show when exam passed but docs not submitted */}
        {steps.exam?.completed && !steps.documents?.completed && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-5 mb-6" data-testid="document-upload-form">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-orange-500" />
              Submit Verification Documents
            </h3>

            {/* ID Proof Type */}
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">ID Proof Type</label>
              <div className="flex gap-2">
                {['aadhaar', 'pan', 'voter_id'].map(type => (
                  <button
                    key={type}
                    onClick={() => setIdProofType(type)}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium border-2 transition-all ${
                      idProofType === type ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-600'
                    }`}
                  >
                    {type === 'aadhaar' ? 'Aadhaar' : type === 'pan' ? 'PAN' : 'Voter ID'}
                  </button>
                ))}
              </div>
            </div>

            {/* ID Number */}
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                {idProofType === 'aadhaar' ? 'Aadhaar Number' : idProofType === 'pan' ? 'PAN Number' : 'Voter ID Number'}
              </label>
              <input
                type="text"
                value={idProofNumber}
                onChange={(e) => setIdProofNumber(e.target.value)}
                placeholder={idProofType === 'aadhaar' ? 'XXXX XXXX XXXX' : idProofType === 'pan' ? 'ABCDE1234F' : 'ABC1234567'}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300"
                data-testid="id-proof-input"
              />
            </div>

            {/* Certificate */}
            <div className="mb-5">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Certificate/Qualification (optional)</label>
              <input
                type="text"
                value={certificateInfo}
                onChange={(e) => setCertificateInfo(e.target.value)}
                placeholder="e.g., Shastri from BHU, Acharya degree"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300"
              />
            </div>

            <button
              onClick={submitDocuments}
              disabled={submitting}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="submit-documents-btn"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              Submit Documents
            </button>
          </div>
        )}

        {/* Pending Review Notice */}
        {steps.admin_review?.status === 'pending_review' && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-center">
            <Clock className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="font-semibold text-blue-800">Under Review</p>
            <p className="text-xs text-blue-600 mt-1">Our team will review your application within 24-48 hours.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Reusable step row component
function StepItem({ icon, label, subtitle, completed, rejected, active, onAction, actionLabel, isLast }) {
  return (
    <div className={`flex items-center gap-4 px-4 py-3.5 ${!isLast ? 'border-b border-stone-50' : ''}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
        completed ? 'bg-green-100 text-green-600' :
        rejected ? 'bg-red-100 text-red-500' :
        active ? 'bg-orange-100 text-orange-600' :
        'bg-stone-100 text-stone-400'
      }`}>
        {completed ? <CheckCircle className="w-5 h-5" /> : rejected ? <XCircle className="w-5 h-5" /> : icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${completed ? 'text-green-700' : rejected ? 'text-red-600' : 'text-slate-800'}`}>{label}</p>
        {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {active && onAction && (
        <button
          onClick={onAction}
          className="px-3 py-1.5 bg-orange-500 text-white text-xs font-semibold rounded-lg hover:bg-orange-600 flex items-center gap-1 flex-shrink-0"
        >
          {actionLabel} <ChevronRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
